import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_SECONDS = 1800; // cap idle tabs at 30 min
const HEARTBEAT_MS = 60000;

const send = (path, seconds) => {
  const token = localStorage.getItem('hibff_auth_token') || sessionStorage.getItem('hibff_auth_token');
  if (!token) return;
  fetch(`${API_URL}/api/telemetry/page`, {
    method: 'POST',
    keepalive: true,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ path, seconds: Math.min(seconds, MAX_SECONDS) }),
  }).catch(() => {});
};

/* Fire-and-forget engagement telemetry — records time spent per page area.
   Powers the Command Centre school engagement heatmap. Aggregate only. */
const PageTelemetry = () => {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const entryRef = useRef({ path: pathname, t: Date.now() });

  // Flush elapsed time for the current page and restart the clock.
  const flush = () => {
    const { path, t } = entryRef.current;
    const seconds = Math.round((Date.now() - t) / 1000);
    if (seconds >= 2) send(path, seconds);
    entryRef.current = { path: entryRef.current.path, t: Date.now() };
  };

  useEffect(() => {
    if (!user) {
      entryRef.current = { path: pathname, t: Date.now() };
      return;
    }
    const prev = entryRef.current;
    if (prev.path !== pathname) {
      const seconds = Math.round((Date.now() - prev.t) / 1000);
      send(prev.path, seconds);
      entryRef.current = { path: pathname, t: Date.now() };
    }
  }, [pathname, user]);

  useEffect(() => {
    if (!user) return undefined;
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    const interval = setInterval(flush, HEARTBEAT_MS);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', flush);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', flush);
    };
  }, [user]);

  return null;
};

export default PageTelemetry;
